// filepath: c:\Users\Puneet Bajaj\Desktop\Ankit\ankitnewform\src\context\AudienceContext.tsx
import { AudienceType } from '@/types/audience';
import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';

// Interface to track persona filters within each segment
export interface SegmentPersonaFilters {
  industryL1: string[];
  industryL2: string[];
  functions: string[];
  roles: string[];
}

// Interface for dynamic segmentation data
export interface DynamicSegmentationData {
  dynamic_segmentation_id: number;
  dynamic_segment_indices: number[];
  persona_ids: number[];
}

export interface AudienceData {
  type: AudienceType | null;
  websiteUrl: string;
  segmentType: 'all' | 'specific' | null;
  specificSegment: string;
  qualitativeInfo: string;
  uploadedFile: File | null;
  audienceId: number | null;
  audienceName: string;
  selectedUseCase: string | null;
  selectedSegments: number[];
  personaFilters: Record<number, SegmentPersonaFilters>;
  // Dynamic segmentation support
  dynamicSegmentationData: DynamicSegmentationData | null;
}

interface AudienceContextType {
  audienceData: AudienceData;
  setAudienceData: React.Dispatch<React.SetStateAction<AudienceData>>;
  updateAudienceData: (data: Partial<AudienceData>) => void;
  resetAudienceData: () => void;
  currentStep: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  isGenerating: boolean;
  setIsGenerating: React.Dispatch<React.SetStateAction<boolean>>;
}

const initialAudienceData: AudienceData = {
  type: null,
  websiteUrl: '',
  segmentType: null,
  specificSegment: '',
  qualitativeInfo: '',
  uploadedFile: null,
  audienceId: null,
  audienceName: '',
  selectedUseCase: null,
  selectedSegments: [],
  personaFilters: {},
  dynamicSegmentationData: null,
};

const AudienceContext = createContext<AudienceContextType | undefined>(undefined);

export const AudienceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [audienceData, setAudienceData] = useState<AudienceData>(initialAudienceData);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  const updateAudienceData = useCallback((data: Partial<AudienceData>) => {
    setAudienceData((prev) => ({ ...prev, ...data }));
  }, []);

  const resetAudienceData = useCallback(() => {
    setAudienceData(initialAudienceData);
  }, []);

  return (
    <AudienceContext.Provider
      value={{
        audienceData,
        setAudienceData,
        updateAudienceData,
        resetAudienceData,
        currentStep,
        setCurrentStep,
        isGenerating,
        setIsGenerating,
      }}
    >
      {children}
    </AudienceContext.Provider>
  );
};

export const useAudience = () => {
  const context = useContext(AudienceContext);
  if (context === undefined) {
    throw new Error('useAudience must be used within an AudienceProvider');
  }
  return context;
};
